/**
 * @file        src/components/SingleDatePicker.jsx
 * @description Shared dark-themed single-date picker. Renders one field button
 *              plus a portaled one-month calendar popover that matches the neon
 *              UI, replacing the native <input type="date"> (whose OS popup can't
 *              be themed). Today is highlighted with a ring; the selected day is
 *              filled neon. Footer has Clear + Today.
 *
 *              Controlled: parent owns `value` as a 'YYYY-MM-DD' string and gets
 *              `onChange(key)` on pick. Pass `minToday` to forbid past dates.
 * @author      Kinetika Labs
 * @created     2026-06-24
 * @changelog
 *   - 2026-08-18 Claude — Icon refresh: the shared glyphs (close, chevrons, check,
 *                          map pin, arrows, search, calendar) redrawn as one
 *                          consistent filled 24×24 family. Path data only — every
 *                          <svg> wrapper, class and size is unchanged.
 *   - 2026-07-16 Claude — Added `openTo`: the month the empty calendar opens at (e.g.
 *                          an adult DOB opens 12 years back, not on today's month).
 *   - 2026-07-16 Claude — Theme fix: replaced hardcoded colors with theme tokens so the screen recolors on Light/Dark switch.
 *   - 2026-06-24 Claude — Initial. Mirrors DateRangePicker styling/positioning.
 *   - 2026-07-06 Claude — Field click TOGGLES the picker (second click closes;
 *                          it only ever opened before, and the outside-click
 *                          handler ignores the field so nothing else closed it).
 *   - 2026-07-13 Claude — Added `min` / `disabled` / `note` so this one picker can
 *                          serve every booking-site date field (PaxModal DOB,
 *                          insurance start/end). Hide "Today" when a future `min`
 *                          makes it unselectable. `hover:bg-ink/10` →
 *                          `hover:bg-white/10` (`ink` is not a defined token, so
 *                          day hover did nothing).
 *   - 2026-07-13 Claude — Added `max`. The year list, the month arrows, the day
 *                          grid and "Today" now all honour min/max, so a passport
 *                          expiry can't offer past years and an issue date can't
 *                          offer future ones.
 *   - 2026-08-10 Claude — Added `error`: a red caption under the value, shown in
 *                          place of `note`. Lets a field accept ANY date and
 *                          explain why it's wrong afterwards, instead of greying
 *                          the day out with no reason given (passport expiry).
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const WD = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

function toKey(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function fromKey(s) {
  if (!s) return null;
  const [y, m, d] = String(s).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function sameDay(a, b) {
  return !!a && !!b && a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function startOfDay(d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
function fmtField(s) {
  const d = fromKey(s);
  if (!d) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * @param {string}   value       'YYYY-MM-DD' (controlled).
 * @param {Function} onChange    called with the picked 'YYYY-MM-DD' ('' on Clear).
 * @param {boolean}  minToday    forbid any past date.
 * @param {string}   [min]       forbid dates before this 'YYYY-MM-DD' (e.g. an
 *                               end date that cannot precede its start). Wins
 *                               over minToday when both are given.
 * @param {string}   [max]       forbid dates after this 'YYYY-MM-DD' (e.g. a
 *                               passport ISSUE date cannot be in the future).
 * @param {boolean}  [disabled]  render read-only (a fixed, derived date).
 * @param {string}   [note]      small caption under the value.
 * @param {string}   [error]     red caption under the value, replacing `note`.
 *                               For rules the picker deliberately does NOT block
 *                               (any date is selectable; the message explains why
 *                               the chosen one is rejected).
 */
export default function SingleDatePicker({
  value,
  onChange,
  label = 'Date',
  placeholder = 'Select date',
  minToday = false,
  min: minProp,
  max: maxProp,
  openTo,
  disabled = false,
  note = '',
  error = '',
  dense = false,
  className = '',
}) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  // With no value, open the calendar at `openTo` when given (e.g. an adult DOB
  // starts 12 years back so the customer isn't paging through a decade of months),
  // else today.
  const [base, setBase] = useState(() => fromKey(value) || fromKey(openTo) || today);
  const [menu, setMenu] = useState(null); // 'month' | 'year' | null (custom dropdowns)

  const fieldRef = useRef(null);
  const popRef = useRef(null);
  const menuRef = useRef(null);

  const sel = fromKey(value);
  // An explicit `min` (e.g. "not before the start date") beats the blanket
  // minToday; with neither, every day is selectable (date of birth).
  const min = fromKey(minProp) || (minToday ? today : null);
  const max = fromKey(maxProp);

  const openPicker = () => {
    if (disabled) return;
    // Toggle: a second click on the field CLOSES the picker. The outside-click
    // handler ignores clicks on the field (so open→outside-mousedown doesn't
    // race the field's own click), which means the field itself must close it.
    if (open) { setOpen(false); return; }
    setBase(fromKey(value) || fromKey(openTo) || today);
    setMenu(null);
    setOpen(true);
  };

  // When a month/year list opens, scroll the current selection into view so a
  // far-back year (e.g. 2015) lands centred instead of off-screen.
  useEffect(() => {
    if (menu && menuRef.current) {
      const el = menuRef.current.querySelector('[data-active="true"]');
      if (el) el.scrollIntoView({ block: 'center' });
    }
  }, [menu]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (ev) => {
      if (popRef.current?.contains(ev.target)) return;
      if (fieldRef.current?.contains(ev.target)) return;
      setOpen(false);
    };
    const onKey = (ev) => { if (ev.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Anchor the popover below the field, re-measuring on scroll/resize; flip up
  // if it would overflow the viewport.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = fieldRef.current?.getBoundingClientRect();
      if (!r) return;
      const w = popRef.current?.offsetWidth || 300;
      const h = popRef.current?.offsetHeight || 360;
      let top = r.bottom + 8;
      if (top + h > window.innerHeight - 8) {
        const above = r.top - h - 8;
        top = above > 8 ? above : Math.max(8, window.innerHeight - h - 8);
      }
      const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
      setRect({ top, left });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const pick = (day) => {
    onChange(toKey(day));
    setOpen(false);
  };

  const year = base.getFullYear();
  const month = base.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysIn = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i += 1) cells.push(null);
  for (let d = 1; d <= daysIn; d += 1) cells.push(new Date(year, month, d));

  const prevDisabled = min && addMonths(base, -1) < startOfDay(new Date(min.getFullYear(), min.getMonth(), 1));
  const nextDisabled = max && addMonths(base, 1) > startOfDay(new Date(max.getFullYear(), max.getMonth(), 1));

  // Quick month / year jump (so a date of birth in e.g. 2015 is one click, not
  // 130 chevron presses). The list never offers a year the bounds forbid: an
  // explicit min/max wins, then minToday keeps it in the future, and with neither
  // it spans 100 years back for birth dates.
  const thisYear = today.getFullYear();
  const minYear = min ? min.getFullYear() : (minToday ? thisYear : thisYear - 100);
  const maxYear = max ? max.getFullYear() : thisYear + (minToday ? 2 : 1);
  const years = [];
  for (let y = maxYear; y >= minYear; y -= 1) years.push(y);
  const jump = (y, m) => setBase(new Date(y, m, 1));

  return (
    <>
      <button
        type="button"
        ref={fieldRef}
        onClick={openPicker}
        disabled={disabled}
        className={
          'field w-full text-left bg-night/70 border transition '
          + (dense ? 'rounded-xl px-3 py-1.5 ' : 'rounded-xl px-4 py-2.5 ')
          + (disabled
            ? 'border-line opacity-60 cursor-not-allowed '
            : 'cursor-pointer ' + (open ? 'border-neon ' : 'border-line hover:border-neon/50 '))
          + className
        }
      >
        {label && <span className={'block font-semibold uppercase tracking-wide text-muted ' + (dense ? 'text-[10px]' : 'text-[11px]')}>{label}</span>}
        <span className={'flex items-center gap-2 ' + (dense ? '' : 'mt-0.5')}>
          <svg viewBox="0 0 24 24" className="w-4 h-4 text-neon shrink-0">
            <path fill="currentColor" d="M7.5 2v2.2h9V2h2.1v2.2h1.9A1.5 1.5 0 0 1 22 5.7V20a1.5 1.5 0 0 1-1.5 1.5h-17A1.5 1.5 0 0 1 2 20V5.7a1.5 1.5 0 0 1 1.5-1.5h1.9V2zM4.1 9.4V19.4h15.8V9.4z" />
          </svg>
          <span className={'text-[15px] ' + (value ? '' : 'text-muted/60')}>{fmtField(value) || placeholder}</span>
        </span>
        {/* The error REPLACES the note: both explain the same rule, and stacking
            them puts the reason twice under one field. */}
        {error
          ? <span className="block text-[11px] font-semibold text-rose-400 mt-0.5">{error}</span>
          : note && <span className="block text-[10px] text-muted mt-0.5">{note}</span>}
      </button>

      {open && rect && createPortal(
        <div
          ref={popRef}
          data-nh-popover=""
          style={{ position: 'fixed', top: rect.top, left: rect.left, zIndex: 80 }}
          className="rounded-2xl border border-line bg-panel shadow-2xl p-4 w-[300px]"
        >
          <div className="flex items-center justify-between mb-3 px-1">
            <button
              type="button"
              onClick={() => setBase(addMonths(base, -1))}
              disabled={prevDisabled}
              className="w-8 h-8 rounded-lg border border-line flex items-center justify-center text-muted hover:text-neon hover:border-neon disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              aria-label="Previous month"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="currentColor" d="M14.6 18.2 8.4 12l6.2-6.2 1.6 1.6L11.6 12l4.6 4.6z" /></svg>
            </button>
            {/* custom themed month / year dropdowns (no ugly native <select>) */}
            <div className="relative flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setMenu((m) => (m === 'month' ? null : 'month'))}
                className={'inline-flex items-center gap-1 font-display font-bold text-ink border rounded-lg px-2.5 py-1 text-sm cursor-pointer transition ' + (menu === 'month' ? 'border-neon' : 'border-line hover:border-neon/60')}
                aria-label="Month"
              >
                {MONTHS[month]}
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-muted"><path fill="currentColor" d="M12 15.4 5.8 9.2l1.6-1.6 4.6 4.6 4.6-4.6 1.6 1.6z" /></svg>
              </button>
              <button
                type="button"
                onClick={() => setMenu((m) => (m === 'year' ? null : 'year'))}
                className={'inline-flex items-center gap-1 font-display font-bold text-ink border rounded-lg px-2.5 py-1 text-sm cursor-pointer transition ' + (menu === 'year' ? 'border-neon' : 'border-line hover:border-neon/60')}
                aria-label="Year"
              >
                {year}
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-muted"><path fill="currentColor" d="M12 15.4 5.8 9.2l1.6-1.6 4.6 4.6 4.6-4.6 1.6 1.6z" /></svg>
              </button>

              {menu && (
                <div
                  ref={menuRef}
                  className={'absolute top-full mt-1.5 z-20 max-h-56 w-36 overflow-y-auto no-scrollbar rounded-xl border border-line bg-panel shadow-2xl p-1 ' + (menu === 'year' ? 'right-0' : 'left-0')}
                >
                  {(menu === 'month'
                    ? MONTHS.map((label, val) => ({ val, label }))
                    : years.map((y) => ({ val: y, label: String(y) }))
                  ).map((o) => {
                    const active = menu === 'month' ? o.val === month : o.val === year;
                    return (
                      <button
                        key={o.val}
                        type="button"
                        data-active={active ? 'true' : undefined}
                        onClick={() => { if (menu === 'month') jump(year, o.val); else jump(o.val, month); setMenu(null); }}
                        className={'w-full text-left px-3 py-1.5 rounded-lg text-sm transition ' + (active ? 'bg-neon/15 text-neon font-semibold' : 'text-ink hover:bg-ink/10')}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setBase(addMonths(base, 1))}
              disabled={nextDisabled}
              className="w-8 h-8 rounded-lg border border-line flex items-center justify-center text-muted hover:text-neon hover:border-neon disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              aria-label="Next month"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="currentColor" d="M9.4 5.8 15.6 12l-6.2 6.2-1.6-1.6L12.4 12 7.8 7.4z" /></svg>
            </button>
          </div>

          <div className="grid grid-cols-7 text-center text-[11px] text-muted mb-1">
            {WD.map((w) => <div key={w} className="py-1">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-y-1">
            {cells.map((day, i) => {
              if (!day) return <div key={`e${i}`} />;
              const disabled = (min && day < min) || (max && day > max);
              const isSel = sameDay(day, sel);
              const isToday = sameDay(day, today);

              let btn = 'w-9 h-9 rounded-lg flex items-center justify-center text-sm transition mx-auto';
              if (disabled) {
                btn += ' text-muted/30 cursor-not-allowed';
              } else if (isSel) {
                btn += ' bg-neon text-onaccent font-bold cursor-pointer shadow-[0_0_0_2px_rgba(52,239,164,.35)]';
              } else {
                btn += ' cursor-pointer hover:bg-ink/10';
                if (isToday) btn += ' text-neon font-bold ring-1 ring-neon/40';
              }

              return (
                <button
                  key={toKey(day)}
                  type="button"
                  disabled={disabled}
                  onClick={() => pick(day)}
                  className={btn}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-line px-1">
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); }}
              className="text-[12px] text-muted hover:text-ink cursor-pointer"
            >
              Clear
            </button>
            {/* "Today" is only offered when today is actually selectable — with a
                future `min` (an end date after its start) or a past `max` (a
                passport issue date) it would set an invalid value. */}
            {!((min && today < min) || (max && today > max)) && (
              <button
                type="button"
                onClick={() => { onChange(toKey(today)); setBase(today); setOpen(false); }}
                className="text-[12px] font-semibold px-4 py-1.5 rounded-lg bg-neon text-onaccent hover:brightness-110 cursor-pointer"
              >
                Today
              </button>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
